import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, DataTable } from "@/components/common";
import { api } from "@/lib/api";
import { ChevronDown, ShieldAlert, Clock } from "lucide-react";

const fmt = new Intl.NumberFormat("tr-TR");

function tsLabel(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 19).replace("T", " ");
  return d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

/** Beyaz liste, belediye başına TEK kez gösterilir — satır satır tekrarlanmaz. */
function WhitelistSummary({ muns, sites }) {
  const grouped = useMemo(() => {
    const g = {};
    for (const m of muns) g[m.id] = { name: m.name, domains: [] };
    for (const s of sites) {
      if (s.status !== "active") continue;
      if (!g[s.municipality_id]) g[s.municipality_id] = { name: "—", domains: [] };
      g[s.municipality_id].domains.push(s.domain);
    }
    return Object.entries(g).filter(([, v]) => v.domains.length > 0);
  }, [muns, sites]);

  if (grouped.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="whitelist-summary">
      <div className="px-4 py-3 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-emerald-600" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">Beyaz Liste</div>
          <div className="font-heading font-semibold text-slate-950 text-sm">Belediye Başına Akredite Domainler</div>
        </div>
      </div>
      {grouped.map(([mid, info]) => (
        <details key={mid} className="group px-4 py-3" data-testid={`whitelist-${mid}`}>
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <span className="text-sm font-medium text-slate-800">{info.name}</span>
            <span className="flex items-center gap-2 text-xs text-slate-500">
              {info.domains.length} domain
              <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
            </span>
          </summary>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {[...info.domains].sort((a, b) => a.localeCompare(b, "tr")).map((d) => (
              <span key={d} className="font-mono text-[11px] bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5">{d}</span>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

export default function UnauthorizedPage() {
  const [rows, setRows] = useState([]);
  const [muns, setMuns] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try {
      const [logs, m, s] = await Promise.all([
        api.get("/admin/reports/unauthorized-domains"),
        api.get("/admin/municipalities"),
        api.get("/admin/publisher-sites"),
      ]);
      setRows(logs.data); setMuns(m.data); setSites(s.data);
    } finally { setLoading(false); }
  })(); }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Güvenlik"
        title="Yetkisiz Domain Logları"
        description="Reklam kodunun izinsiz kullanıldığı domainler burada kayıt altına alınır. Aynı domain için tek satır tutulur, tekrar sayısı sayaçta birikir."
      />

      <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="unauthorized-ttl-note">
        <Clock className="w-3.5 h-3.5 shrink-0" />
        Kayıtlar <b className="mx-1">72 saat</b> sonra otomatik olarak silinir — log şişmesi olmaz.
      </div>

      <WhitelistSummary muns={muns} sites={sites} />

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
        <DataTable
          testid="unauthorized-table"
          defaultSort={{ key: "last-seen", dir: "desc" }}
          columns={[
            {
              label: "Belediye", sortKey: "mun", sortValue: (r) => r.municipality_name || "",
              render: (r) => <div><div className="text-sm">{r.municipality_name}</div><div className="text-[11px] text-slate-500">{r.placement_name}</div></div>,
            },
            {
              label: "Yetkisiz Domain", sortKey: "domain", sortValue: (r) => r.detected_domain || "",
              render: (r) => <span className="font-mono text-xs text-red-700 font-semibold">{r.detected_domain || "—"}</span>,
            },
            {
              label: "Tespit Edilen Sayfa URL", sortKey: "url", sortValue: (r) => r.page_url || "",
              render: (r) => (
                <span className="block max-w-sm truncate font-mono text-[11px] text-slate-600" title={r.page_url}>
                  {r.page_url || "—"}
                </span>
              ),
            },
            {
              label: "Adet", sortKey: "hits", sortValue: (r) => r.hit_count || 0,
              render: (r) => <span className="font-semibold tabular-nums">{fmt.format(r.hit_count || 0)}</span>,
            },
            {
              label: "Son Görülme", sortKey: "last-seen", sortValue: (r) => new Date(r.last_seen || r.created_at || 0).getTime(),
              render: (r) => <span className="font-mono text-xs">{tsLabel(r.last_seen || r.created_at)}</span>,
            },
          ]}
          rows={rows}
          empty="Henüz yetkisiz kullanım kaydı yok."
        />
      )}
    </div>
  );
}
